import type { FC, ReactNode } from 'react'

import { Fragment, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface HeadingLine {
    text: string
    italic?: boolean
}

interface ScrollLitHeadingProps {
    id?: string
    lines: Array<HeadingLine>
    className?: string
}

/*
 * A display heading whose words darken one by one as it scrolls into view.
 * The server renders every word fully dark, so the heading is always readable
 * without JavaScript; the scroll effect only takes over after mount.
 */
export const ScrollLitHeading: FC<ScrollLitHeadingProps> = ({ id, lines, className }): ReactNode => {
    const ref = useRef<HTMLHeadingElement>(null)
    const total = lines.reduce((count, line) => count + line.text.split(' ').length, 0)
    const [lit, setLit] = useState(total)

    useEffect(() => {
        const element = ref.current

        if (!element) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        let frame = 0

        const update = () => {
            frame = 0

            const rect = element.getBoundingClientRect()
            const start = window.innerHeight * 0.9
            const end = window.innerHeight * 0.42
            const progress = Math.min(1, Math.max(0, (start - rect.top) / (start - end)))

            setLit(Math.round(progress * total))
        }

        const onScroll = () => {
            if (!frame) frame = window.requestAnimationFrame(update)
        }

        update()

        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)

        return () => {
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)

            if (frame) window.cancelAnimationFrame(frame)
        }
    }, [total])

    let wordIndex = 0

    return (
        <h2 id={id} ref={ref} className={cn('font-heading', className)}>
            {lines.map((line, lineIndex) => (
                <span key={lineIndex} className={cn('block', line.italic && 'italic')}>
                    {line.text.split(' ').map((word, index, words) => {
                        const isLit = wordIndex < lit

                        wordIndex += 1

                        return (
                            <Fragment key={index}>
                                <span className={cn('transition-colors duration-500', isLit ? 'text-foreground' : 'text-pebble')}>
                                    {word}
                                </span>

                                {index < words.length - 1 && ' '}
                            </Fragment>
                        )
                    })}
                </span>
            ))}
        </h2>
    )
}
